import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('dronemaps_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dronemaps_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Drones API
export const dronesApi = {
  getAll: () => api.get('/drones'),
  get: (id) => api.get(`/drones/${id}`),
  create: (data) => api.post('/drones', data),
  update: (id, data) => api.put(`/drones/${id}`, data),
  delete: (id) => api.delete(`/drones/${id}`),
};

// Missions API
export const missionsApi = {
  getAll: () => api.get('/missions'),
  get: (id) => api.get(`/missions/${id}`),
  create: (data) => api.post('/missions', data),
  delete: (id) => api.delete(`/missions/${id}`),
};

// Airspace API
export const airspaceApi = {
  getZones: () => api.get('/airspace/zones'),
  checkIntersection: (geometry) => api.post('/airspace/check', geometry),
  getZonesAtPoint: (lat, lng) => api.get('/airspace/zones-at-point', { params: { lat, lng } }),
};

// Subscriptions API
export const subscriptionsApi = {
  getCurrent: () => api.get('/subscriptions/current'),
  getPlans: () => api.get('/subscriptions/plans'),
};

// Payments API
export const paymentsApi = {
  createCheckout: (planType, originUrl, promoCode = null) => 
    api.post('/payments/checkout', { plan_type: planType, origin_url: originUrl, promo_code: promoCode }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Stats API
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard'),
};

// NOTAM API
export const notamApi = {
  getByIcao: (icao) => api.get('/notams/live', { params: { icao } }),
  getByLocation: (lat, lng, radius = 50000) => 
    api.get('/notams', { params: { lat, lng, radius } }),
};

// Weather API — drone briefing (Open-Meteo + NOAA SWPC proxy)
export const weatherApi = {
  get: ({ lat, lon, date, time = '10:00', ceClass }) =>
    api.get('/weather', { params: { lat, lon, date, time, ce_class: ceClass } }),
};

// Environmental Zones API
export const environmentalApi = {
  getZones: (lat, lng, radius = 20000, types = 'natura2000,rnc,pn,pnr') =>
    api.get('/environmental-zones', { params: { lat, lng, radius, zone_types: types } }),
};

// DFS German Airspace API
export const dfsAirspaceApi = {
  getInfo: (lat, lng, zoom = 10) =>
    api.get('/dfs-airspace-info', { params: { lat, lng, zoom } }),
};

// DIPUL German Drone Restriction Zones API
export const dipulApi = {
  getInfo: (lat, lng, zoom = 10) =>
    api.get('/dipul-zone-info', { params: { lat, lng, zoom } }),
};

// OpenAIP European Airspace API (CTR, TMA, P/D/R zones)
export const openAipApi = {
  getInfo: (lat, lng, dist = 10000) =>
    api.get('/openaip-airspace-info', { params: { lat, lng, dist } }),
};

// Natura 2000 EEA API (European Protected Areas)
export const natura2000Api = {
  getInfo: (lat, lng, radius_km = 5) =>
    api.get('/natura2000/info', { params: { lat, lng, radius_km } }),
};

// DGAC Altitude Restrictions API (French IGN/DGAC layer)
export const dgacApi = {
  getAltitudeRestriction: (lat, lng) =>
    api.get('/dgac-altitude-restriction', { params: { lat, lng } }),
};

// PDF Downloads API
export const pdfApi = {
  logDownload: (missionId, missionType) => 
    api.post('/pdf-downloads', { mission_id: missionId, mission_type: missionType }),
  getHistory: (limit = 20) => api.get('/pdf-downloads', { params: { limit } }),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  exportBackup: () => api.get('/admin/backup/export', { responseType: 'blob', timeout: 120000 }),
  getMissions: (params = {}) => api.get('/admin/missions', { params }),
  getDrones: (params = {}) => api.get('/admin/drones', { params }),
  // Blog admin
  getBlogPosts: (params = {}) => api.get('/admin/blog/posts', { params }),
  createBlogPost: (data) => api.post('/admin/blog/posts', data),
  updateBlogPost: (id, data) => api.put(`/admin/blog/posts/${id}`, data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/posts/${id}`),
  generateBlogFromPrompt: (data) => api.post('/admin/blog/generate-from-prompt', data, { timeout: 90000 }),
  generateBlogBatch: (data) => api.post('/admin/blog/generate-batch', data, { timeout: 240000 }),
  translateBlogPostAll: (postId) => api.post(`/admin/blog/posts/${postId}/translate-all`, null, { timeout: 240000 }),
  // Blog cover image — accepts a pre-built FormData so the caller controls
  // the file field. Timeout raised to 60s because the backend re-encodes
  // the image via Pillow before responding.
  uploadBlogImage: (formData) => api.post('/admin/blog/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }),
  generateBlogImage: (data) => api.post('/admin/blog/generate-image', data, { timeout: 120000 }),

  // ED-269 UAS Geographical Zones
  uploadEd269: (country, file, replace = true) => {
    const fd = new FormData();
    fd.append('country', country);
    fd.append('replace', String(replace));
    fd.append('file', file);
    return api.post('/admin/ed269/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  deleteEd269: (country) => api.delete(`/admin/ed269/${country}`),
  getUasZonesCountries: () => api.get('/uas-zones/countries'),
};

// Blog public API
export const blogApi = {
  getPosts: (params = {}) => api.get('/blog/posts', { params }),
  getPost: (slug, lang) => api.get(`/blog/posts/${slug}`, { params: lang ? { lang } : {} }),
  getCategories: () => api.get('/blog/categories'),
};

export default api;
